/**
 * Single active patient context per clinician, across all tabs (Requirement 1).
 *
 * A clinician's tabs form a cohort that shares one authoritative "active
 * context" record in localStorage, coordinated over a same-origin
 * BroadcastChannel with a `storage`-event fallback. The invariant: at most one
 * patient context is *live* (rendering PHI) at any moment. When a second, different
 * patient is launched, the launching tab detects the conflict and the clinician
 * resolves it — switch (which supersedes and locks the other tabs) or cancel.
 *
 * Liveness is tracked by a lightweight owner heartbeat: the owning tab refreshes
 * `updatedAt` while visible; a record older than STALE_MS is treated as abandoned
 * (e.g. the owning tab crashed), so a new launch may claim it without nagging.
 *
 * This is a UX/consistency layer. The authoritative PHI boundary is the
 * server-side session→clinician owner binding (see smart_fhir/routes.py).
 */

import { currentClinicianKey } from './smartStorageScope';

export const ACTIVE_CONTEXT_KEY = 'march:smart:activeContext';
export const STALE_MS = 15_000;
const HEARTBEAT_MS = 5_000;

export type ActiveContext = {
  sessionId: string;
  patientId: string;
  patientLabel: string;
  epoch: number;
  ownerTabId: string;
  updatedAt: number;
};

export type ClaimInput = {
  sessionId: string;
  patientId: string;
  patientLabel: string;
};

export type ClaimDecision = { action: 'claim' } | { action: 'conflict'; existing: ActiveContext };

// ---------------------------------------------------------------------------
// Pure decision core (unit-tested without any browser globals)
// ---------------------------------------------------------------------------

/** True when an existing record's owner is still alive (heartbeat is fresh). */
export function isOwnerLive(existing: ActiveContext, now: number): boolean {
  return now - existing.updatedAt < STALE_MS;
}

/**
 * Decide what a tab should do when it establishes `incoming` as its patient.
 * - No record, or the same patient → claim (become/refresh the owner).
 * - A different patient whose owner is stale → claim (abandoned record).
 * - A different patient with a live owner → conflict (clinician must resolve).
 */
export function decideClaim(
  existing: ActiveContext | null,
  incoming: Pick<ClaimInput, 'sessionId' | 'patientId'>,
  now: number,
): ClaimDecision {
  if (!existing) return { action: 'claim' };
  if (existing.patientId === incoming.patientId) return { action: 'claim' };
  if (!isOwnerLive(existing, now)) return { action: 'claim' };
  return { action: 'conflict', existing };
}

/**
 * True when `mine` has been superseded by the current active context — a
 * different session at a strictly higher epoch. The losing tab must lock.
 */
export function isSuperseded(
  active: ActiveContext | null,
  mine: { sessionId: string; epoch: number } | null,
): boolean {
  if (!active || !mine) return false;
  return active.sessionId !== mine.sessionId && active.epoch > mine.epoch;
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readActiveContext(): ActiveContext | null {
  const store = safeLocalStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(ACTIVE_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveContext>;
    if (!parsed || typeof parsed.sessionId !== 'string' || typeof parsed.epoch !== 'number') {
      return null;
    }
    return {
      sessionId: parsed.sessionId,
      patientId: String(parsed.patientId ?? ''),
      patientLabel: String(parsed.patientLabel ?? ''),
      epoch: parsed.epoch,
      ownerTabId: String(parsed.ownerTabId ?? ''),
      updatedAt: Number(parsed.updatedAt ?? 0),
    };
  } catch {
    return null;
  }
}

function writeActiveContext(ctx: ActiveContext): void {
  const store = safeLocalStorage();
  if (!store) return;
  try {
    store.setItem(ACTIVE_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    // Best-effort coordination state.
  }
}

// ---------------------------------------------------------------------------
// Coordinator (side-effecting singleton, one per tab)
// ---------------------------------------------------------------------------

export type ConflictSignal = { targetSessionId: string; newPatientLabel: string };

type ChannelMessage =
  | { type: 'context-changed'; epoch: number }
  // A tab is *deliberating* a conflict against `targetSessionId` (the live tab):
  // that live tab shows a passive banner while the launching tab's clinician
  // decides. Cleared on resolve. Transient — BroadcastChannel only, not persisted.
  | { type: 'conflict-pending'; targetSessionId: string; newPatientLabel: string }
  | { type: 'conflict-cleared'; targetSessionId: string };

function newTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    // Fall through to a non-crypto id.
  }
  return `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

class PatientContextCoordinator {
  readonly tabId = newTabId();
  private mine: ActiveContext | null = null;
  private channel: BroadcastChannel | null = null;
  private channelKey = '';
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private readonly supersedeListeners = new Set<(active: ActiveContext) => void>();
  private readonly conflictPendingListeners = new Set<(signal: ConflictSignal) => void>();
  private readonly conflictClearedListeners = new Set<(signal: { targetSessionId: string }) => void>();

  private ensureChannel(): void {
    const key = currentClinicianKey() || 'anon';
    if (this.channel && this.channelKey === key) return;
    this.teardownChannel();
    this.channelKey = key;
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this.channel = new BroadcastChannel(`march:smart:ctx:${key}`);
      this.channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
        const data = event.data;
        if (!data) return;
        if (data.type === 'context-changed') {
          this.checkSuperseded();
        } else if (data.type === 'conflict-pending') {
          for (const listener of this.conflictPendingListeners) {
            listener({ targetSessionId: data.targetSessionId, newPatientLabel: data.newPatientLabel });
          }
        } else if (data.type === 'conflict-cleared') {
          for (const listener of this.conflictClearedListeners) {
            listener({ targetSessionId: data.targetSessionId });
          }
        }
      };
    } catch {
      this.channel = null;
    }
  }

  private teardownChannel(): void {
    try {
      this.channel?.close();
    } catch {
      // ignore
    }
    this.channel = null;
  }

  private post(message: ChannelMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch {
      // ignore
    }
  }

  private onStorage = (event: StorageEvent): void => {
    if (event.key === ACTIVE_CONTEXT_KEY) this.checkSuperseded();
  };

  private startListening(): void {
    this.ensureChannel();
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorage);
    }
  }

  private checkSuperseded(): void {
    if (!this.mine) return;
    const active = readActiveContext();
    if (isSuperseded(active, this.mine) && active) {
      this.stopHeartbeat();
      this.mine = null;
      for (const listener of this.supersedeListeners) listener(active);
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    if (typeof window === 'undefined') return;
    this.heartbeat = setInterval(() => {
      if (!this.mine) return;
      // Only the current owner heartbeats, and only while the tab is visible.
      const current = readActiveContext();
      if (!current || current.ownerTabId !== this.tabId) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      this.mine = { ...this.mine, updatedAt: Date.now() };
      writeActiveContext(this.mine);
    }, HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  /** Evaluate whether this tab may take `input` as its live patient context. */
  claim(input: ClaimInput): ClaimDecision {
    this.startListening();
    const existing = readActiveContext();
    const decision = decideClaim(existing, input, Date.now());
    if (decision.action === 'claim') {
      this.commit(input, existing && existing.patientId === input.patientId ? existing.epoch : (existing?.epoch ?? 0) + 1);
    }
    return decision;
  }

  /** Force this tab to become the owner, superseding any other patient context. */
  supersede(input: ClaimInput): void {
    const existing = readActiveContext();
    this.commit(input, (existing?.epoch ?? 0) + 1);
  }

  private commit(input: ClaimInput, epoch: number): void {
    this.startListening();
    this.mine = {
      sessionId: input.sessionId,
      patientId: input.patientId,
      patientLabel: input.patientLabel,
      epoch,
      ownerTabId: this.tabId,
      updatedAt: Date.now(),
    };
    writeActiveContext(this.mine);
    this.post({ type: 'context-changed', epoch });
    this.startHeartbeat();
  }

  /** Tell the live owner tab that a conflicting launch is being deliberated. */
  announceConflict(targetSessionId: string, newPatientLabel: string): void {
    this.startListening();
    this.post({ type: 'conflict-pending', targetSessionId, newPatientLabel });
  }

  /** Clear a previously-announced conflict (the launching clinician resolved it). */
  clearConflict(targetSessionId: string): void {
    this.post({ type: 'conflict-cleared', targetSessionId });
  }

  onConflictPending(listener: (signal: ConflictSignal) => void): () => void {
    this.conflictPendingListeners.add(listener);
    this.startListening();
    return () => this.conflictPendingListeners.delete(listener);
  }

  onConflictCleared(listener: (signal: { targetSessionId: string }) => void): () => void {
    this.conflictClearedListeners.add(listener);
    this.startListening();
    return () => this.conflictClearedListeners.delete(listener);
  }

  /** This tab is no longer showing a patient (logout / disconnect / lock). */
  release(): void {
    this.stopHeartbeat();
    const active = readActiveContext();
    // Only clear the shared record if we still own it — never stomp a newer owner.
    if (active && this.mine && active.ownerTabId === this.tabId && active.epoch === this.mine.epoch) {
      const store = safeLocalStorage();
      try {
        store?.removeItem(ACTIVE_CONTEXT_KEY);
      } catch {
        // ignore
      }
    }
    this.mine = null;
  }

  onSuperseded(listener: (active: ActiveContext) => void): () => void {
    this.supersedeListeners.add(listener);
    this.startListening();
    return () => this.supersedeListeners.delete(listener);
  }
}

let singleton: PatientContextCoordinator | null = null;

export function getPatientContextCoordinator(): PatientContextCoordinator {
  if (!singleton) singleton = new PatientContextCoordinator();
  return singleton;
}
